import { 
  collection, 
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  query, 
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';

const COLLECTION = 'sponsors';

/**
 * Criar novo patrocínio/banner
 */
export const createSponsor = async (sponsorData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTION), {
      ...sponsorData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating sponsor:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Buscar todos os patrocínios ativos
 * @param {boolean} activeOnly - Se deve retornar apenas ativos
 */
export const getSponsors = async (activeOnly = true) => {
  try {
    let q = collection(db, COLLECTION);
    const constraints = [];

    if (activeOnly) {
      constraints.push(where('isActive', '==', true));
      // Não retornar expirados
      constraints.push(where('expiresAt', '>', new Date().toISOString()));
    }

    constraints.push(orderBy('priority', 'desc')); // Maior prioridade primeiro

    q = query(q, ...constraints);
    const querySnapshot = await getDocs(q);
    
    const sponsors = [];
    querySnapshot.forEach((doc) => {
      sponsors.push({ id: doc.id, ...doc.data() });
    });

    return { success: true, data: sponsors };
  } catch (error) {
    console.error('Error getting sponsors:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Selecionar patrocínio aleatório com base na prioridade
 * Prioridade mais alta = maior chance de aparecer
 */
export const getRandomSponsor = async () => {
  try {
    const result = await getSponsors(true);
    
    if (!result.success || result.data.length === 0) {
      return { success: false, error: 'Nenhum patrocínio disponível' };
    }

    const sponsors = result.data;

    // Criar array ponderado pela prioridade
    const weightedSponsors = [];
    sponsors.forEach(sponsor => {
      const weight = sponsor.priority || 1;
      for (let i = 0; i < weight; i++) {
        weightedSponsors.push(sponsor);
      }
    });

    // Selecionar aleatoriamente
    const randomIndex = Math.floor(Math.random() * weightedSponsors.length);
    const selectedSponsor = weightedSponsors[randomIndex];

    return { success: true, data: selectedSponsor };
  } catch (error) {
    console.error('Error getting random sponsor:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Selecionar múltiplos patrocínios únicos
 * @param {number} count - Quantidade de patrocínios a retornar
 */
export const getRandomSponsors = async (count = 3) => {
  try {
    const result = await getSponsors(true);
    
    if (!result.success || result.data.length === 0) {
      return { success: true, data: [] };
    }

    const sponsors = result.data;
    const selected = [];
    const available = [...sponsors];

    // Selecionar 'count' patrocínios únicos
    for (let i = 0; i < Math.min(count, available.length); i++) {
      // Criar array ponderado
      const weighted = [];
      available.forEach(sponsor => {
        const weight = sponsor.priority || 1;
        for (let j = 0; j < weight; j++) {
          weighted.push(sponsor);
        }
      });

      // Selecionar aleatoriamente
      const randomIndex = Math.floor(Math.random() * weighted.length);
      const selectedSponsor = weighted[randomIndex];
      
      selected.push(selectedSponsor);
      
      // Remover dos disponíveis para não repetir
      const indexToRemove = available.findIndex(s => s.id === selectedSponsor.id);
      if (indexToRemove !== -1) {
        available.splice(indexToRemove, 1);
      }
    }

    return { success: true, data: selected };
  } catch (error) {
    console.error('Error getting random sponsors:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Atualizar patrocínio
 */
export const updateSponsor = async (sponsorId, updates) => {
  try {
    const docRef = doc(db, COLLECTION, sponsorId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating sponsor:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Deletar patrocínio
 */
export const deleteSponsor = async (sponsorId) => {
  try {
    const docRef = doc(db, COLLECTION, sponsorId);
    await deleteDoc(docRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting sponsor:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Buscar patrocínio de uma barbearia específica
 */
export const getSponsorByBarbershop = async (barbershopId) => {
  try {
    const q = query(
      collection(db, COLLECTION),
      where('type', '==', 'barbershop'),
      where('barbershopId', '==', barbershopId),
      where('isActive', '==', true)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return { success: false, error: 'Patrocínio não encontrado' };
    }

    const sponsor = {
      id: querySnapshot.docs[0].id,
      ...querySnapshot.docs[0].data()
    };

    return { success: true, data: sponsor };
  } catch (error) {
    console.error('Error getting barbershop sponsor:', error);
    return { success: false, error: error.message };
  }
};
